import React, { useMemo } from 'react'
import { Cell, colors, H5, LayoutBand, List, Row, Subheading } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import PersonDisplay from './PersonDisplay'
import { sortPersonsByPrimary } from '../utils/display.utils'

/**
 * FloatingPersonsList
 *
 * Renders persons that are not attached to any household ("floating" persons) as a list,
 * preceded by a header band. Sorts persons so the primary person appears first and highlights
 * the currently highlighted person. Renders nothing when there are no floating persons.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.persons - Array of floating person objects to display
 * @param {Function} [props.onPersonSelect] - Callback when a person in the list is selected
 * @returns {JSX.Element|null} The floating persons list, or null when there are no persons
 */
const FloatingPersonsList = ({ persons = [], onPersonSelect }) => {
  const [t] = useTranslation()
  const { highlightedPersonId } = useR2StateContext()

  const sortedPersons = useMemo(() => sortPersonsByPrimary(persons), [persons])

  if (sortedPersons.length === 0) return null

  return (
    <>
      <LayoutBand color={colors.gray02} marginY="xs">
        <Row>
          <Cell growX alignY="middle">
            <H5>
              {t('household-list.names-not-in-household', 'Names not in a household')}
              <Subheading size="xs" secondary>
                {t(
                  'household-list.names-not-in-household-description',
                  'Open a name to review it, or rearrange names in Edit Households.'
                )}
              </Subheading>
            </H5>
          </Cell>
        </Row>
      </LayoutBand>
      <List>
        {sortedPersons.map((person) => (
          <PersonDisplay
            key={person?.id}
            person={person}
            isSelected={highlightedPersonId === person?.id}
            onPersonSelect={onPersonSelect}
          />
        ))}
      </List>
    </>
  )
}

export default FloatingPersonsList
